<script>
import { MOLDURA_DO_THREAD } from '@modules/aawz-intelligence-v2/ui-registry/moldura-do-thread'
import { montarRespostasPareadas } from '@modules/aawz-intelligence-v2/utils/perguntas-ao-gestor'
import { BLOCO_PERGUNTAS_AO_GESTOR } from '@modules/aawz-intelligence-v2/constants/TextChat'

export default {
  name: 'IntelligenceV2ManagerQuestions',
  props: {
    // `output.perguntas` da tool — `[{ pergunta, porque? }]`, no máximo 5.
    perguntas: {
      type: Array,
      default: function () {
        return []
      },
    },
    // Só o último turno aceita resposta. Turno antigo vira registro do que foi
    // perguntado: responder ali mandaria a resposta para o fim da conversa, longe
    // do contexto em que a pergunta foi feita.
    editavel: {
      type: Boolean,
      default: false,
    },
    enviando: {
      type: Boolean,
      default: false,
    },
    // Respostas JÁ enviadas, alinhadas por índice com `perguntas`. Não colide
    // com `respostas` do data: aquele é o que está sendo digitado agora.
    respostasEnviadas: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  data() {
    return {
      MOLDURA_DO_THREAD,
      // Mapa `indice -> texto`. Índice, não o texto da pergunta: duas perguntas
      // podem repetir o enunciado.
      respostas: {},
      // Só vale quando o bloco NÃO é editável (ver `mostraCorpo`): é o gestor
      // reabrindo um formulário já respondido para reler o que foi perguntado.
      reaberto: false,
    }
  },
  computed: {
    titulo() {
      return BLOCO_PERGUNTAS_AO_GESTOR.titulo
    },
    legenda() {
      return BLOCO_PERGUNTAS_AO_GESTOR.legenda
    },
    rotuloEnvio() {
      return BLOCO_PERGUNTAS_AO_GESTOR.enviar
    },
    placeholder() {
      return BLOCO_PERGUNTAS_AO_GESTOR.placeholder
    },
    semResposta() {
      return BLOCO_PERGUNTAS_AO_GESTOR.semResposta
    },
    respondido() {
      return this.respostasEnviadas.length > 0
    },
    itens() {
      const enviadas = this.respostasEnviadas
      return this.perguntas.map(function (item, indice) {
        const enviada = String(enviadas[indice] || '').trim()
        return {
          indice: indice,
          chave: 'pergunta-' + indice,
          pergunta: item.pergunta,
          // O `porque` explica por que vale responder — depois de respondida ele
          // não tem mais função e só competiria com a resposta.
          porque: enviada ? '' : item.porque || '',
          enviada: enviada,
        }
      })
    },
    // Quantas de fato foram respondidas — em branco continua em aberto, e a
    // fita precisa dizer isso sem o gestor ter de expandir.
    resumoRespondidas() {
      const total = this.perguntas.length
      const respondidas = this.itens.filter(function (item) {
        return Boolean(item.enviada)
      }).length
      return respondidas + ' de ' + total + ' respondidas'
    },
    legendaDaFita() {
      return this.respondido ? this.resumoRespondidas : this.legenda
    },
    // Respondido (ou turno antigo) colapsa na fita, como a `raciocinio-linha`:
    // o formulário já cumpriu o papel e vira registro do que foi perguntado,
    // sem ocupar a altura de um form no meio do histórico. Continua alcançável
    // por clique. Editável = aberto, e o cabeçalho nem responde a clique — não
    // faz sentido esconder o campo que o gestor precisa preencher agora.
    mostraCorpo() {
      return this.editavel || this.reaberto
    },
    classeSeta() {
      return this.reaberto ? 'ia-block-seta--aberta' : ''
    },
    classeCabecalho() {
      return this.editavel ? '' : 'ia-block-card__cabecalho--clicavel'
    },
    // Lista vazia não vira card vazio — mesma regra do `open-questions`.
    temPerguntas() {
      return this.itens.length > 0
    },
    // Responder uma já vale: o gestor pode não ter todas as definições agora, e
    // exigir o formulário inteiro trava o turno por causa da pergunta que ele
    // não sabe responder.
    podeEnviar() {
      if (!this.editavel || this.enviando) return false
      return Object.keys(this.respostas).some(function (chave) {
        return String(this.respostas[chave] || '').trim().length > 0
      }, this)
    },
  },
  methods: {
    // `$set` porque `respostas` nasce vazio: sem ele o Vue 2 não observa a chave
    // nova e o botão de enviar não reage ao primeiro caractere digitado.
    aoDigitar(indice, valor) {
      this.$set(this.respostas, indice, valor)
    },
    alternar() {
      if (this.editavel) return
      this.reaberto = !this.reaberto
    },
    enviar() {
      if (!this.podeEnviar) return
      const texto = montarRespostasPareadas(this.perguntas, this.respostas)
      if (!texto) return
      this.$emit('responder', texto)
    },
  },
}
</script>

<template>
  <div v-if="temPerguntas" class="manager-questions ia-block-card" v-bind="MOLDURA_DO_THREAD">
    <!-- `button` (não `div`) sempre que colapsa: o cabeçalho é o único jeito de
         reabrir o formulário respondido, e num `div` ele não recebe foco nem
         responde a Enter/Espaço. Mesmo elemento que a `raciocinio-linha` usa. -->
    <component
      :is="editavel ? 'div' : 'button'"
      :type="editavel ? null : 'button'"
      class="ia-block-card__cabecalho ia-block-card__cabecalho--fita"
      :class="classeCabecalho"
      :aria-expanded="editavel ? null : String(reaberto)"
      @click="alternar"
    >
      <!-- Mesmo `question` do `open-questions` — os dois blocos falam de
           pergunta, e um glifo diferente para o mesmo conceito confunde. -->
      <icon-svg
        name="question"
        :size="14"
        color="var(--primary)"
        class="ia-block-card__icone"
      />
      <span class="ia-block-card__titulo">{{ titulo }}</span>
      <span class="ia-block-card__meta">{{ legendaDaFita }}</span>
      <icon-svg
        v-if="!editavel"
        name="arrow-down-new-ux"
        :size="14"
        color="var(--neutral-700)"
        class="ia-block-seta"
        :class="classeSeta"
      />
    </component>

    <div
      v-for="item in itens"
      v-show="mostraCorpo"
      :key="item.chave"
      class="manager-questions__item"
    >
      <span class="manager-questions__corpo">
        <label class="manager-questions__pergunta ia-texto-auxiliar" :for="item.chave">
          {{ item.pergunta }}
        </label>
        <span v-if="item.porque" class="manager-questions__porque ia-texto-auxiliar">
          {{ item.porque }}
        </span>
        <span v-if="item.enviada" class="manager-questions__resposta ia-texto-auxiliar">
          {{ item.enviada }}
        </span>
        <span
          v-else-if="respondido"
          class="manager-questions__sem-resposta ia-texto-auxiliar"
        >
          {{ semResposta }}
        </span>
        <v-textarea
          v-if="editavel"
          :id="item.chave"
          :value="respostas[item.indice] || ''"
          class="manager-questions__campo no-border"
          :placeholder="placeholder"
          rows="1"
          auto-grow
          hide-details
          dense
          :disabled="enviando"
          @input="aoDigitar(item.indice, $event)"
        />
      </span>
    </div>

    <!-- Seção secundária: o que o painel deixou em aberto e NÃO é do gestor.
         Read-only de propósito — não há campo porque ele não tem como
         responder; é registro do que ficou pendente com as áreas. -->
    <div v-if="editavel" class="manager-questions__acoes">
      <BaseButton
        primary
        :disabled="!podeEnviar"
        :loading="enviando"
        @click="enviar"
      >
        {{ rotuloEnvio }}
      </BaseButton>
    </div>
  </div>
</template>

<style lang="sass" scoped>
// Card, faixa, icone, titulo e meta vem de `.ia-block-card*` (partial de
// modulo) — o mesmo desenho de `open-questions` e `next-steps`. Aqui fica so o
// que e proprio deste bloco: a linha da pergunta e o rodape de acao.

// Separador ate a borda do card, como nos irmaos — por isso o item nao usa
// `.ia-block-card__corpo` e paga o proprio padding.
// O cabeçalho vira `button` quando colapsa; sem isto ele herdaria o desenho
// nativo de botão (borda, fundo, fonte) e a fita ficaria diferente das outras.
.ia-block-card__cabecalho
  width: 100%
  text-align: left
  font: inherit
  border: 0

// O reset acima ganha da partial por especificidade (o `scoped` soma um seletor
// de atributo), e levava junto a linha inferior da fita. Redeclarada aqui, com o
// MESMO token de `_ia-block-card.sass`.
.ia-block-card__cabecalho--fita
  border-bottom: 1px solid var(--border-hairline)

.manager-questions__item + .manager-questions__item
  border-top: 1px solid var(--border-hairline)

.manager-questions__item
  display: flex
  align-items: flex-start
  padding: 10px 14px

.manager-questions__corpo
  display: flex
  flex-direction: column
  gap: 2px
  min-width: 0
  flex: 1

// Sem superficie o campo fica INVISIVEL: `no-border` tira a borda e o fundo do
// card e branco, entao nao havia nada dizendo onde escrever (visto na tela).
// O tom e o MESMO da fita do cabecalho (e do circulo numerado): um material so
// para o que nao e conteudo, contrastando com o branco do card.
.manager-questions__resposta
  color: var(--neutral-900)
  margin-top: 4px
  white-space: pre-wrap

// Em aberto continua legível como pendência, não como resposta vazia.
.manager-questions__sem-resposta
  color: var(--neutral-600)
  margin-top: 4px
  font-style: italic

.manager-questions__campo
  margin-top: 6px
  padding: 0
  background: var(--neutral-100)
  border: 1px solid var(--border-hairline)
  border-radius: 8px

  ::v-deep textarea
    padding: 8px 10px
    font-size: var(--ia-font-auxiliar)
    line-height: 1.5

.manager-questions__acoes
  display: flex
  justify-content: flex-end
  padding: 10px 14px
  border-top: 1px solid var(--border-hairline)

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.manager-questions__pergunta
  color: var(--neutral-900)

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.manager-questions__porque
  color: var(--neutral-600)

</style>
